import type { CmmdBoardTask, CmmdMember, CmmdProjectAgentMember } from "@cmmd-forge/contracts";
import type { ProjectTaskPickerOption } from "./ProjectTaskPicker";

export function assigneeOptions(
  task: CmmdBoardTask,
  members: readonly CmmdMember[],
  agentMembers: readonly CmmdProjectAgentMember[],
): readonly ProjectTaskPickerOption[] {
  const options = members.map<ProjectTaskPickerOption>((member) => ({
    value: `human:${member.id}`,
    label: member.name,
    group: "People",
    visual: { kind: "avatar", name: member.name, src: member.avatarUrl },
  }));
  const agents = agentMembers.map<ProjectTaskPickerOption>((member) => ({
    value: `agent:${member.id}`,
    label: `${member.displayName} · ${member.agentId}`,
    shortLabel: member.displayName,
    group: member.lifecycleStatus === "active" ? "Agent Members" : "Inactive Agent Members",
    disabled:
      member.lifecycleStatus !== "active" &&
      !(task.assignment?.kind === "agent_member" && task.assignment.agentMemberId === member.id),
    visual: { kind: "agent", agentKind: member.agentKind },
  }));
  const humanAssignment =
    task.assignment?.kind === "human_user"
      ? task.assignment
      : task.assignment === null && task.assignee !== null
        ? { kind: "human_user" as const, userId: task.assignee }
        : null;
  const missingHuman =
    humanAssignment && !members.some((member) => Number(member.id) === humanAssignment.userId)
      ? [
          {
            value: `human:${humanAssignment.userId}`,
            label: task.assigneeName ?? `User #${humanAssignment.userId}`,
            group: "People",
            visual: {
              kind: "avatar" as const,
              name: task.assigneeName ?? `User #${humanAssignment.userId}`,
              src: null,
            },
          },
        ]
      : [];
  const agentAssignment = task.assignment?.kind === "agent_member" ? task.assignment : null;
  const missingAgent =
    agentAssignment && !agentMembers.some((member) => member.id === agentAssignment.agentMemberId)
      ? [
          {
            value: `agent:${agentAssignment.agentMemberId}`,
            label: `${agentAssignment.displayName} · ${agentAssignment.agentId}`,
            shortLabel: agentAssignment.displayName,
            group: "Agent Members",
            visual: { kind: "agent" as const, agentKind: agentAssignment.agentKind },
          },
        ]
      : [];
  return [...missingHuman, ...missingAgent, ...options, ...agents];
}

/**
 * Owner is a person, never an agent: it answers "who is accountable", which an
 * agent cannot be. Assignee answers "who is doing it", which an agent usually
 * is — so the two fields deliberately draw from different populations.
 */
export function ownerOptions(
  task: CmmdBoardTask,
  members: readonly CmmdMember[],
): readonly ProjectTaskPickerOption[] {
  const options = members.map<ProjectTaskPickerOption>((member) => ({
    value: String(member.id),
    label: member.name,
    visual: { kind: "avatar", name: member.name, src: member.avatarUrl },
  }));
  if (task.ownerId === null || members.some((member) => Number(member.id) === task.ownerId)) {
    return options;
  }
  // An owner who has left the workspace still owns the record; dropping them
  // from the list would silently reassign on the next edit.
  const label = task.ownerName ?? `User #${task.ownerId}`;
  return [
    {
      value: String(task.ownerId),
      label,
      visual: { kind: "avatar", name: label, src: task.ownerAvatar },
    },
    ...options,
  ];
}

// The single-value `assignmentValue` / `assignmentSubject` pair was removed
// when the detail rail moved to multi-select. Reading the selection is now
// `assignmentSelectionValues` and writing it is `planAssignmentSelectionChange`,
// both in projectTaskAssignmentSelection.ts — a value/subject round-trip cannot
// express "one of several assignees changed". Task creation keeps its own
// single-subject helper in projectTaskCreateAssignment.ts, which is correct:
// a task being created has no existing assignees to preserve.
